import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from './common/SafeIcon';
import OptimizedImage from './components/OptimizedImage';
import LazySection from './components/LazySection';
import QuoteCalculator from './pages/QuoteCalculator';
import { preloadResources, throttle } from './utils/performance';

const { FiPhone, FiMail, FiSettings, FiTool, FiZap, FiCheckCircle, FiArrowRight, FiShield, FiCpu, FiActivity, FiCalculator } = FiIcons;
const { FaWhatsapp } = FaIcons;

const Home = ({ whatsappLink, logoUrl }) => {
  const services = [
    { icon: FiTool, title: "Manutenzione Preventiva", description: "Programmi di manutenzione periodica per evitare fermi macchina costosi e prolungare la vita dei componenti." },
    { icon: FiZap, title: "Riparazione Elettronica", description: "Interventi specializzati su PLC, azionamenti, motori brushless e schede di controllo di ogni marca." },
    { icon: FiSettings, title: "Sostituzione E/O Revisione", description: "Sostituzione cuscinetti mandrino, viti a ricircolo di sfere e ripristino geometria assi certificata." }
  ];

  return (
    <>
      <section className="bg-slate-900 text-white py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1504917595217-d4dc5f649776?w=1600')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="inline-block px-4 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm font-bold mb-6 border border-blue-600/30 uppercase tracking-widest">
                Specialisti CNC Bergamo
              </div>
              <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
                Mars Systems: <br /><span className="text-blue-500">Precisione</span> in Movimento.
              </h1>
              <p className="text-xl text-slate-300 mb-10 max-w-lg leading-relaxed">
                Assistenza tecnica specializzata, riparazione e manutenzione avanzata per macchine utensili a controllo numerico.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calcola-preventivo" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all shadow-xl shadow-blue-500/20">
                  <SafeIcon icon={FiCalculator} /> Richiedi Preventivo
                </Link>
                <a href={whatsappLink} className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all backdrop-blur-sm border border-white/10">
                  <SafeIcon icon={FaWhatsapp} /> Contatto Rapido
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block relative mt-10 lg:mt-0">
              <div className="relative">
                {/* Glow effect matching the blue branding */}
                <div className="absolute -inset-4 bg-blue-500/30 blur-3xl rounded-full"></div>
                <img
                  src="https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvcG5n/af96fef325f049547c6c253baaaf1c7c.png"
                  alt="Mars Systems CNC Automation"
                  className="relative z-10 w-full h-auto rounded-3xl shadow-2xl border border-white/5 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      <LazySection className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-black text-slate-900 mb-6">Massimizziamo la tua Produttività</h2>
            <p className="text-lg text-slate-600">Offriamo un servizio completo a 360° per garantire che il tuo parco macchine sia sempre operativo e performante al 100%.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {services.map((s, idx) => (
              <div key={idx} className="group p-8 bg-slate-50 rounded-3xl border border-slate-100 hover:border-blue-500/30 hover:bg-white hover:shadow-2xl transition-all duration-300">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/20">
                  <SafeIcon icon={s.icon} className="text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </LazySection>

      <LazySection className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl"></div>
              {/* Nuova immagine Precision Mechanics */}
              <img 
                src="https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvcG5n/8a71eb0629c2fadf2c5274a888fb5e8a.png" 
                alt="Precision Mechanics Tools and Gears" 
                className="w-full h-auto object-cover rounded-3xl shadow-2xl relative z-10 border border-white/10" 
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-8 italic">"Esperienza e dedizione sulle principali marche: Fanuc, Siemens, Heidenhain, Mitsubishi."</h2>
              <div className="space-y-6">
                {["Diagnostica avanzata PLC/CNC", "Retrofit e aggiornamento sistemi obsoleti", "Check-up completo geometria assi", "Interventi certificati Mars Systems"].map((f, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-sm text-white" />
                    </div>
                    <span className="text-lg text-slate-300">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </LazySection>
    </>
  );
};

function App() {
  const whatsappLink = "https://wa.me/393498408946?text=Buongiorno%20Mars%20Systems%2C%20avrei%20bisogno%20di%20un%20preventivo%20per%20assistenza%20tecnica";
  const [isScrolled, setIsScrolled] = useState(false);

  const logoUrl = "https://media-manager-c.questera.ai/greta-media/32dc413054558dbd6d4d3c4a3e2032d0a5a88f3a3f9e6c5dd10a15135c65ae90de62d22e50b183c419625584e3716ff8/images/aW1hZ2UvcG5n/4f28dfded00b0f418000d71d512d8157.png";

  useEffect(() => {
    preloadResources();
    const handleScroll = throttle(() => setIsScrolled(window.scrollY > 10), 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-white font-sans">
        <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-xl py-3' : 'bg-transparent py-5'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center gap-4 group">
                <img src={logoUrl} alt="Mars Systems" className={`h-12 md:h-16 object-contain transition-all duration-300 group-hover:scale-105 ${!isScrolled ? 'brightness-0 invert' : ''}`} />
                <div className="flex flex-col justify-center border-l-2 border-blue-500/30 pl-3">
                  <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-black text-blue-500">CNC Technical</span>
                  <span className={`text-[10px] md:text-xs uppercase tracking-[0.2em] font-bold ${isScrolled ? 'text-slate-500' : 'text-slate-300'}`}>Support</span>
                </div>
              </Link>
              <div className="flex items-center gap-4">
                <a href="tel:3498408946" className={`hidden md:flex items-center gap-2 font-black transition-colors ${isScrolled ? 'text-slate-900 hover:text-blue-600' : 'text-white hover:text-blue-400'}`}>
                  <SafeIcon icon={FiPhone} /> 349 840 8946
                </a>
                <Link to="/calcola-preventivo" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-lg shadow-blue-600/20 transition-all hover:scale-105">
                  PREVENTIVO RAPIDO
                </Link>
              </div>
            </div>
          </div>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<Home whatsappLink={whatsappLink} logoUrl={logoUrl} />} />
            <Route path="/calcola-preventivo" element={<QuoteCalculator logoUrl={logoUrl} />} />
          </Routes>
        </main>
        <footer className="bg-slate-950 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="inline-block mb-10">
              <img src={logoUrl} alt="Mars Systems" className="h-24 object-contain brightness-0 invert opacity-90" />
            </div>
            <div className="grid md:grid-cols-3 gap-8 mb-16 text-left border-y border-white/5 py-12">
              <div>
                <h4 className="font-bold text-blue-500 mb-4 uppercase text-sm tracking-widest">Sede Operativa</h4>
                <p className="text-slate-400">Ubiale Clanezzo,<br />via Bondo 2 (BG)</p>
              </div>
              <div>
                <h4 className="font-bold text-blue-500 mb-4 uppercase text-sm tracking-widest">Contatti Diretti</h4>
                <p className="text-slate-400">T: +39 349 840 8946<br />E: marssystems25@gmail.com</p>
              </div>
              <div>
                <h4 className="font-bold text-blue-500 mb-4 uppercase text-sm tracking-widest">Orari Assistenza</h4>
                <p className="text-slate-400">Lun - Ven: 08:00 - 18:00<br />Sabato: 08:00 - 12:00</p>
              </div>
            </div>
            <p className="text-slate-500 text-xs tracking-wider uppercase">Mars Systems - Specialist in CNC Machines Maintenance & Support.</p>
          </div>
        </footer>
        <a href={whatsappLink} className="fixed bottom-6 right-6 bg-green-500 text-white w-16 h-16 rounded-2xl flex items-center justify-center shadow-[0_10px_30px_rgba(34,197,94,0.4)] z-50 hover:scale-110 transition-all hover:-rotate-6 whatsapp-pulse">
          <SafeIcon icon={FaWhatsapp} className="text-3xl" />
        </a>
      </div>
    </Router>
  );
}

export default App;